import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config/api';
import './LoanApplication.css';

const LoanApplication = () => {
    const navigate = useNavigate();
    const [loanType, setLoanType] = useState('cash');
    const [amount, setAmount] = useState('');
    const [duration, setDuration] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [bankName, setBankName] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [guarantors, setGuarantors] = useState([{
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        relationship: 'colleague'
    }]);
    const [error, setError] = useState('');
    const [checkingActiveLoan, setCheckingActiveLoan] = useState(true);
    const [hasActiveLoan, setHasActiveLoan] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(0); // milliseconds

    useEffect(() => {
        const checkActiveLoan = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setCheckingActiveLoan(false);
                    return;
                }

                // Check if user is admin and redirect
                try {
                    const userRes = await axios.get(`${API_URL}/api/users/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (userRes.data.role === 'admin') {
                        navigate('/admin');
                        return;
                    }
                } catch (err) {
                    console.error('Error checking user role:', err);
                }

                const res = await axios.get(`${API_URL}/api/loans`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const loans = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data.loans : res.data.loans || []);
                // If any loan has a status in blockingStatuses, treat it as active
                const blockingStatuses = ['pending', 'approved', 'disbursed', 'repaying', 'defaulted'];
                const active = loans.some(l => blockingStatuses.includes(String(l.status).toLowerCase()) || blockingStatuses.includes(l.status));
                setHasActiveLoan(active);

                // Check latest rejected loan and compute cooldown remaining
                const rejectedLoans = loans.filter(l => String(l.status).toLowerCase() === 'rejected' && l.rejectedAt);
                if (rejectedLoans.length > 0) {
                    const latest = rejectedLoans.reduce((a, b) => new Date(a.rejectedAt) > new Date(b.rejectedAt) ? a : b);
                    const now = Date.now();
                    const rejectedAt = new Date(latest.rejectedAt).getTime();
                    const cooldownMs = 24 * 60 * 60 * 1000;
                    const elapsed = now - rejectedAt;
                    if (elapsed < cooldownMs) {
                        setCooldownRemaining(cooldownMs - elapsed);
                    } else {
                        setCooldownRemaining(0);
                    }
                }
            } catch (err) {
                // ignore errors here but keep checking flag false so form can be used
                console.error('Error checking loans', err?.response || err);
            } finally {
                setCheckingActiveLoan(false);
            }
        };

        checkActiveLoan();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/loans/products`);
                setProducts(res.data);
            } catch (err) {
                console.error('Error fetching products:', err);
            }
        };

        fetchProducts();
    }, []);

    // Additional frontend safety check for admin role
    const [userRole, setUserRole] = useState(null);
    
    useEffect(() => {
        const checkUserRole = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const res = await axios.get(`${API_URL}/api/users/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUserRole(res.data.role);
                } catch (err) {
                    console.error('Error checking user role:', err);
                }
            }
        };
        checkUserRole();
    }, []);

    if (userRole === 'admin') {
        return (
            <div className="loan-app container">
                <h1>Loan Application</h1>
                <div className="error" role="alert">
                    Administrators cannot apply for loans. Please use your admin dashboard to manage loan applications.
                </div>
                <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => navigate('/admin')}>Go to Admin Dashboard</button>
                </div>
            </div>
        );
    }

    const handleGuarantorChange = (index, field, value) => {
        const newGuarantors = [...guarantors];
        newGuarantors[index][field] = value;
        setGuarantors(newGuarantors);
    };

    const handleProductChange = (productId) => {
        setSelectedProduct(productId);
        const product = products.find(p => p._id === productId);
        if (product) {
            setAmount(product.price.toString());
        }
    };

    const handleAddGuarantor = () => {
        if (guarantors.length < 2) {
            setGuarantors([...guarantors, {
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                relationship: 'colleague'
            }]);
        }
    };

    const handleRemoveGuarantor = (index) => {
        if (guarantors.length > 1) {
            const newGuarantors = [...guarantors];
            newGuarantors.splice(index, 1);
            setGuarantors(newGuarantors);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (hasActiveLoan) {
            setError('You already have an active loan. Please finish repayment before applying for another.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            await axios.post(
                `${API_URL}/api/loans`,
                {
                    type: loanType,
                    amount: parseInt(amount),
                    duration: parseInt(duration),
                    accountNumber,
                    bankName,
                    guarantors
                },
                {
                    headers: {
      Authorization: `Bearer ${token}`,
    },
                }
            );

            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.msg || 'An error occurred');
        }
    };

    if (userRole === 'admin') {
        return (
            <div className="loan-app container">
                <h1>Loan Application</h1>
                <div className="error" role="alert">
                    Administrators cannot apply for loans. Please use your admin dashboard to manage loan applications.
                </div>
                <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => navigate('/admin')}>Go to Admin Dashboard</button>
                </div>
            </div>
        );
    }

    if (checkingActiveLoan) {
        return (
            <div className="loan-app container">
                <h1>Loan Application</h1>
                <p className="muted">Checking your loan status…</p>
            </div>
        );
    }

    if (hasActiveLoan) {
        return (
            <div className="loan-app container">
                <h1>Loan Application</h1>
                <div className="error" role="status">
                    You already have an active loan. Please finish repayment before applying for another loan.
                </div>
                <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>View my loans</button>
                </div>
            </div>
        );
    }

    if (cooldownRemaining > 0) {
        const hours = Math.floor(cooldownRemaining / (60 * 60 * 1000));
        const minutes = Math.ceil((cooldownRemaining % (60 * 60 * 1000)) / (60 * 1000));
        return (
            <div className="loan-app container">
                <h1>Loan Application</h1>
                <div className="error" role="status">
                    Your previous application was rejected. Please wait {hours} hour(s) and {minutes} minute(s) before re-applying.
                </div>
                <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>View my loans</button>
                </div>
            </div>
        );
    }

    return (
        <div className="loan-app container">
            <h1>Loan Application</h1>

            {error && <div className="error" role="alert">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <label className="form-label">Loan Type</label>
                    <select
                        value={loanType}
                        onChange={e => {
                            setLoanType(e.target.value);
                            setSelectedProduct('');
                            setAmount('');
                        }}
                        className="form-select"
                        aria-label="Loan type"
                    >
                        <option value="cash">Cash Loan</option>
                        <option value="solar">Solar Loan</option>
                    </select>
                </div>

                {loanType === 'solar' ? (
                    <>
                        <div className="form-row">
                            <label className="form-label">Select Solar Product</label>
                            <select
                                value={selectedProduct}
                                onChange={e => handleProductChange(e.target.value)}
                                className="form-select"
                                required
                            >
                                <option value="">Choose a product...</option>
                                {products.map(product => (
                                    <option key={product._id} value={product._id}>
                                        {product.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedProduct && (
                            <>
                                <div className="form-row">
                                    <label className="form-label">Price (₦)</label>
                                    <input
                                        type="text"
                                        value={amount}
                                        className="form-input"
                                        readOnly
                                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                    />
                                </div>
                                <div className="form-row">
                                    <label className="form-label">Product Features</label>
                                    <div style={{ 
                                        padding: '12px', 
                                        backgroundColor: '#f9f9f9', 
                                        border: '1px solid #ddd', 
                                        borderRadius: '4px' 
                                    }}>
                                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                            {products.find(p => p._id === selectedProduct)?.features.map((feature, index) => (
                                                <li key={index} style={{ marginBottom: '4px' }}>{feature}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <div className="form-row">
                        <label className="form-label">Amount (₦)</label>
                        <input
                            type="number"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            className="form-input"
                            required
                        />
                    </div>
                )}

                <div className="form-row">
                    <label className="form-label">Duration (months)</label>
                    <input
                        type="number"
                        value={duration}
                        onChange={e => setDuration(e.target.value)}
                        className="form-input"
                        min="1"
                        max="6"
                        required
                    />
                </div>

                <div className="form-row">
                    <label className="form-label">Account Number</label>
                    <input
                        type="text"
                        value={accountNumber}
                        onChange={e => setAccountNumber(e.target.value)}
                        className="form-input"
                        placeholder="Enter your account number"
                        required
                    />
                </div>

                <div className="form-row">
                    <label className="form-label">Bank Name</label>
                    <input
                        type="text"
                        value={bankName}
                        onChange={e => setBankName(e.target.value)}
                        className="form-input"
                        placeholder="Enter your bank name"
                        required
                    />
                </div>

                {guarantors.map((guarantor, index) => (
                    <div key={index} className="guarantor-card">
                        <h3>Guarantor Information</h3>
                        <div className="grid-2">
                            <div>
                                <label className="form-label">First Name</label>
                                <input
                                    type="text"
                                    value={guarantor.firstName}
                                    onChange={e => handleGuarantorChange(index, 'firstName', e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div>
                                <label className="form-label">Last Name</label>
                                <input
                                    type="text"
                                    value={guarantor.lastName}
                                    onChange={e => handleGuarantorChange(index, 'lastName', e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                        </div>
                        <div className="grid-2">
                            <div>
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    value={guarantor.email}
                                    onChange={e => handleGuarantorChange(index, 'email', e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div>
                                <label className="form-label">Phone Number</label>
                                <input
                                    type="tel"
                                    value={guarantor.phoneNumber}
                                    onChange={e => handleGuarantorChange(index, 'phoneNumber', e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <label className="form-label">Relationship</label>
                            <select
                                value={guarantor.relationship}
                                onChange={e => handleGuarantorChange(index, 'relationship', e.target.value)}
                                className="form-select"
                                aria-label={`Relationship for guarantor ${index + 1}`}
                            >
                                <option value="colleague">Colleague</option>
                            </select>
                        </div>
                    </div>
                ))}

                <div style={{ marginTop: 14 }}>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                    >
                        Submit Application
                    </button>
                </div>
            </form>
        </div>
    );
};

export default LoanApplication;
